import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import { workProjects, workCategories, industries } from "../data/portfolio";
import { ProjectCard, IndexTable } from "../components/card";
import { Chip } from "../components/bits";

export default function Work() {
  const [params, setParams] = useSearchParams();
  const c = params.get("c") || "all";
  const i = params.get("i") || "all";
  const view = params.get("view") || "grid";
  const q = (params.get("q") || "").toLowerCase();

  const set = (key, val) => {
    const p = new URLSearchParams(params);
    if (!val || val === "all") p.delete(key); else p.set(key, val);
    setParams(p);
  };

  const filtered = useMemo(() => workProjects.filter((p) => {
    if (c !== "all" && p.category !== c && !(p.also || []).includes(c)) return false;
    if (i !== "all" && p.industry !== i) return false;
    if (q && !(`${p.title} ${p.client} ${(p.deliverables || []).join(" ")}`.toLowerCase().includes(q))) return false;
    return true;
  }), [c, i, q]);

  const catCount = (id) => workProjects.filter((p) => p.category === id || (p.also || []).includes(id)).length;

  return (
    <div className="page-x pt-28 pb-24" data-testid="work-page">
      <Helmet><title>Work — Poonam Baid | vdesigns.eps</title></Helmet>
      <div className="wrap">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <h1 className="display" style={{ fontSize: "clamp(2.6rem,9vw,7rem)", color: "var(--ink)" }}>WORK <span className="serif" style={{ color: "var(--blue)" }}>({workProjects.length})</span></h1>
        </div>
        <p style={{ width: "100%", fontSize: "clamp(1.05rem,2.2vw,1.9rem)", lineHeight: 1.25, margin: "16px 0 26px" }}>Brand systems, campaigns, packaging, social, print and motion — client and agency work. Pitches are tagged.</p>

        {/* filters */}
        <div className="md:sticky md:top-[52px] z-[60] py-4 -mx-[var(--page-x)] px-[var(--page-x)]" style={{ background: "rgba(242,240,234,.85)", backdropFilter: "blur(10px)", borderBottom: "1px solid var(--rule)" }}>
          <div className="flex gap-2 flex-nowrap md:flex-wrap items-center overflow-x-auto noscroll">
            <Chip active={c === "all"} onClick={() => set("c", "all")} count={workProjects.length} testid="chip-all">All</Chip>
            {workCategories.map((cat) => (
              <Chip key={cat.id} active={c === cat.id} onClick={() => set("c", cat.id)} count={catCount(cat.id)} testid={`chip-${cat.id}`}>{cat.label}</Chip>
            ))}
            <div className="ml-auto flex gap-2 shrink-0">
              <Chip active={view === "grid"} onClick={() => set("view", "grid")} testid="view-grid">Grid</Chip>
              <Chip active={view === "list"} onClick={() => set("view", "list")} testid="view-list">List</Chip>
            </div>
          </div>
          <div className="flex gap-2 flex-nowrap md:flex-wrap mt-2 overflow-x-auto noscroll">
            <Chip active={i === "all"} onClick={() => set("i", "all")} testid="ind-all">All industries</Chip>
            {industries.map((ind) => (
              <Chip key={ind} active={i === ind} onClick={() => set("i", ind)} count={workProjects.filter((p) => p.industry === ind).length} testid={`ind-${ind.split(" ")[0].toLowerCase()}`}>{ind}</Chip>
            ))}
          </div>
        </div>

        <p className="mono my-6" style={{ color: "var(--grey)" }}>{filtered.length} file{filtered.length !== 1 ? "s" : ""}</p>

        {view === "list" ? (
          <IndexTable items={filtered} testid="work-list" />
        ) : (
          <div style={{ columnGap: 20 }} className="[column-count:1] sm:[column-count:2] lg:[column-count:3] xl:[column-count:4]" data-testid="work-grid">
            {filtered.map((p, idx) => (
              <div key={p.slug} className="mb-6" style={{ breakInside: "avoid" }}>
                <ProjectCard project={p} eager={idx < 4} />
              </div>
            ))}
          </div>
        )}

        {filtered.length === 0 && (
          <div className="py-20 text-center">
            <p className="lead">No files match. <button className="ul" style={{ color: "var(--blue)" }} onClick={() => setParams(new URLSearchParams())}>Reset filters</button></p>
          </div>
        )}

        <div className="mt-16 rule-t pt-8">
          <Link to="/playground" className="h2 ul" style={{ fontSize: "clamp(24px,3vw,44px)" }}>Can't find it? See the Playground →</Link>
        </div>
      </div>
    </div>
  );
}
